<template>
  <div v-if="hasAnyPermission('sap-connection-view')" class="py-4 container-fluid">
    <ToastPrime position="bottom-left" />
    <div class="card">
      <div class="card-header py-3">
        <div class="row align-items-center">
          <!-- Bagian H% di Kiri -->
          <div class="col-4">
            <h5 class="mb-0">SAP Connection</h5>
          </div>

          <!-- Bagian Pencarian di Tengah -->
          <div class="col-4 d-flex justify-content-center">
            <IconField class="w-100">
              <InputIcon class="pi pi-search" />
              <InputText v-model="table.search" type="text" size="small" placeholder="Search Connection"
                class="rounded-pill w-100" @keyup="filter" />
            </IconField>
          </div>

          <!-- Bagian Button di Kanan -->
          <div class="col-4 d-flex justify-content-end align-items-center gap-2">
            <SelectPrime v-if="hasClientData" v-model="table.clientFilter" :options="clientFilterOptions"
              option-label="label" option-value="value" placeholder="Filter Client" class="rounded-pill" size="small"
              style="width: auto;">
              <template #value="slotProps">
                <div class="d-flex align-items-center">
                  <i class="pi pi-filter me-2" :class="slotProps.value ? 'text-primary' : 'text-muted'"
                    style="font-size: 0.8rem;" />
                  <div :class="{ 'text-muted': !slotProps.value }">{{ slotProps.value || slotProps.placeholder }}</div>
                </div>
              </template>
            </SelectPrime>
            <button v-if="hasAnyPermission('sap-connection-create')" type="button"
              class="mx-1 mb-0 btn btn-sm btn-primary" @click="openAddModal">Add</button>
          </div>
        </div>
      </div>
      <div style="height: 500px; overflow-y: auto">
        <DataTable :value="table.data" removable-sort scrollable scroll-height="flex" table-style="min-width: 100rem">
          <Column header="Client" header-class="bg-light">
            <template #body="row">
              <Skeleton v-if="table.onLoad" />
              <span v-else>{{ row.data.client_relation?.description }}</span>
            </template>
          </Column>
          <Column header="Connection" header-class="bg-light">
            <template #body="row">
              <Skeleton v-if="table.onLoad" />
              <span v-else>{{ row.data.connection }}</span>
            </template>
          </Column>

          <Column header="Host" header-class="bg-light">
            <template #body="row">
              <Skeleton v-if="table.onLoad" />
              <span v-else class="d-inline-flex align-items-center gap-2">
                <span>{{ isFieldVisible(row.data.id, 'host') ? (row.data.data?.host || '') :
                  maskValue(row.data.data?.host) }}</span>
                <i v-if="row.data.data?.host" class="pi"
                  :class="isFieldVisible(row.data.id, 'host') ? 'pi-eye' : 'pi-eye-slash'" style="cursor: pointer;"
                  @click="toggleField(row.data.id, 'host')" />
              </span>
            </template>
          </Column>
          <Column header="Port" header-class="bg-light">
            <template #body="row">
              <Skeleton v-if="table.onLoad" />
              <span v-else class="d-inline-flex align-items-center gap-2">
                <span>{{ isFieldVisible(row.data.id, 'port') ? (row.data.data?.port || '') :
                  maskValue(row.data.data?.port) }}</span>
                <i v-if="row.data.data?.port" class="pi"
                  :class="isFieldVisible(row.data.id, 'port') ? 'pi-eye' : 'pi-eye-slash'" style="cursor: pointer;"
                  @click="toggleField(row.data.id, 'port')" />
              </span>
            </template>
          </Column>
          <Column header="Instance" header-class="bg-light">
            <template #body="row">
              <Skeleton v-if="table.onLoad" />
              <span v-else>{{ row.data.data?.instance || '' }}</span>
            </template>
          </Column>
          <Column header="Username" header-class="bg-light">
            <template #body="row">
              <Skeleton v-if="table.onLoad" />
              <span v-else class="d-inline-flex align-items-center gap-2">
                <span>{{ isFieldVisible(row.data.id, 'username') ? (row.data.data?.username || '') :
                  maskValue(row.data.data?.username) }}</span>
                <i v-if="row.data.data?.username" class="pi"
                  :class="isFieldVisible(row.data.id, 'username') ? 'pi-eye' : 'pi-eye-slash'" style="cursor: pointer;"
                  @click="toggleField(row.data.id, 'username')" />
              </span>
            </template>
          </Column>
          <Column header="Status" header-class="bg-light">
            <template #body="row">
              <Skeleton v-if="table.onLoad" />
              <span v-else :class="row.data.default === 1 ? 'badge badge-success' : 'badge badge-danger'">{{
                row.data.default === 1 ? "Default" : "Not Default" }}</span>
            </template>
          </Column>
          <Column field="actions" header="Actions" header-class="bg-light text-dark" sortable>
            <template v-if="table.onLoad" #body>
              <Skeleton></Skeleton>
            </template>
            <template v-else #body="row">
              <div v-if="table.data.length > 0 && !table.onLoad">
                <ButtonPrime v-if="hasAnyPermission('sap-connection-update')" v-tooltip.bottom="'Edit'"
                  icon="pi pi-pencil" outlined rounded size="small" @click="openEditModal(row.data)" />
                <ButtonPrime v-if="hasAnyPermission('sap-connection-delete')" v-tooltip.bottom="'Delete'"
                  icon="pi pi-trash" outlined rounded severity="danger" size="small" style="margin-left: 5px;"
                  @click="deleteItem(row.data.id)" />
                <ButtonPrime v-if="hasAnyPermission('sap-connection-set-default')" v-tooltip.bottom="'Set Default'"
                  icon="pi pi-sliders-h" outlined rounded severity="help" size="small" style="margin-left: 5px;"
                  @click="setDefault(row.data.id)" />
              </div>
              <div v-else class="m-4"><span /></div>
            </template>
          </Column>
        </DataTable>
      </div>
      <div class="card-footer pb-0 d-flex justify-content-between align-items-center">
        <BasePagination v-model="table.page.current_page" :page-count="table.page.total_page" size="sm"
          @click="changePage()" />
        <p>
          {{ (table.page.current_page - 1) * table.page.per_page + table.data.length }}
          of {{ table.total }}
        </p>
      </div>
    </div>

    <DialogPrime v-model:visible="modal.visible" :header="modal.title" style="width: 45rem; margin: 10px;">
      <div class="mb-2 row">
        <div class="col-md-6 col-sm-12">
          <label for="client" class="font-semibold">Client <span class="text-danger">*</span></label>
          <SelectPrime id="client" v-model="modal.data.client" :options="optClient" option-label="description"
            option-value="code" placeholder="Please choose client"
            :invalid="modal.errorMessage['client'] && modal.data.client === ''" class="w-full md:w-56" fluid />
          <small v-if="modal.errorMessage['client'] && modal.data.client == ''" class="text-danger">{{
            modal.errorMessage['client'] }}</small>
        </div>

        <div class="col-md-6 col-sm-12">
          <label for="connection" class="font-semibold">Connection <span class="text-danger">*</span></label>
          <InputText id="connection" v-model="modal.data.connection" placeholder="Connection"
            :class="`${(modal.errorMessage['connection'] && modal.data.connection == '') ? 'border-danger' : ''}`" fluid
            @input="toUpperCaseVal($event, 'connection')" />
          <small v-if="modal.errorMessage['connection'] && modal.data.connection == ''" class="text-danger">{{
            modal.errorMessage['connection'] }}</small>
        </div>
      </div>

      <div class="mb-2 row">
        <div class="col-md-4 col-sm-12">
          <label for="host" class="font-semibold">Host <span class="text-danger">*</span></label>
          <InputText id="host" v-model="modal.data.host" placeholder="Host"
            :class="`${(modal.errorMessage['host'] && modal.data.host == '') ? 'border-danger' : ''}`" fluid
            @input="toUpperCaseVal($event, 'host')" />
          <small v-if="modal.errorMessage['host'] && modal.data.host == ''" class="text-danger">{{
            modal.errorMessage['host'] }}</small>
        </div>
        <div class="col-md-4 col-sm-12">
          <label for="port" class="font-semibold">Port <span class="text-danger">*</span></label>
          <InputText id="port" v-model="modal.data.port" placeholder="Port"
            :class="`${(modal.errorMessage['port'] && modal.data.port == '') ? 'border-danger' : ''}`" fluid
            @input="toUpperCaseVal($event, 'port')" />
          <small v-if="modal.errorMessage['port'] && modal.data.port == ''" class="text-danger">{{
            modal.errorMessage['port'] }}</small>
        </div>
        <div class="col-md-4 col-sm-12">
          <label for="instance" class="font-semibold">Instance <span class="text-danger">*</span></label>
          <InputText id="instance" v-model="modal.data.instance" placeholder="Instance"
            :class="`${(modal.errorMessage['instance'] && modal.data.instance == '') ? 'border-danger' : ''}`" fluid />
          <small v-if="modal.errorMessage['instance'] && modal.data.instance == ''" class="text-danger">{{
            modal.errorMessage['instance'] }}</small>
        </div>
      </div>

      <div class="row">
        <div class="col-md-6 col-sm-12">
          <label for="username" class="font-semibold">Username <span class="text-danger">*</span></label>
          <InputText id="username" v-model="modal.data.username" placeholder="Username"
            :class="`${(modal.errorMessage['username'] && modal.data.username == '') ? 'border-danger' : ''}`" fluid />
          <small v-if="modal.errorMessage['username'] && modal.data.username == ''" class="text-danger">{{
            modal.errorMessage['username'] }}</small>
        </div>

        <div class="col-md-6 col-sm-12">
          <label for="password" class="font-semibold">
            Password <span v-if="modal.action === 'create'" class="text-danger">*</span>
          </label>
          <Password id="password" v-model="modal.data.password"
            :placeholder="modal.action === 'edit' ? 'Leave blank to keep current password' : 'Password'"
            :invalid="modal.errorMessage['password'] && modal.data.password == ''" toggle-mask fluid />
          <small v-if="modal.errorMessage['password'] && modal.data.password == ''" class="text-danger d-block">{{
            modal.errorMessage['password'] }}</small>
        </div>
      </div>

      <template #footer>
        <div class="d-flex justify-content-end gap-2 pt-3 border-top w-100 mt-2 px-1">
          <ButtonPrime type="button" label="Cancel" severity="danger" class="px-4 border-0" raised size="small"
            :disabled="modal.onLoading" @click="modal.visible = false"></ButtonPrime>
          <ButtonPrime
            v-if="(modal.action === 'create' && hasAnyPermission('sap-connection-create')) || (modal.action === 'edit' && hasAnyPermission('sap-connection-update'))"
            type="button" severity="contrast" raised size="small" label="Save" class="px-4" :disabled="modal.onLoading"
            @click="save()">
            <template v-if="modal.onLoading" #icon>
              <i class="fas fa-spinner fa-spin me-2"></i>
            </template>
          </ButtonPrime>
        </div>
      </template>
    </DialogPrime>
  </div>
  <NoAccess v-else />
</template>

<script>
import { mapState } from "vuex";
import api from "@/helpers/api";
import sapConnectionService from "@/services/sap/sapConnectionService"
import clientService from "@/services/generalMasterData/clientService"
import NoAccess from "@/components/NoAccess.vue";

export default {
  components: {
    NoAccess,
  },
  data() {
    return {
      clientCode: '',
      companyCode: '',
      table: {
        search: this.$route.query.search ? this.$route.query.search : "",
        clientFilter: this.$route.query.client || null,
        clientOptions: [],
        onLoad: false,
        total: 0,
        data: [],
        page: {
          current_page: 1,
          total_page: 1,
          per_page: 100,
        },
        limit: 100,
        loadTimeout: null,
      },
      modal: {
        visible: false,
        action: "",
        title: "",
        data: {},
        errorMessage: {},
        onLoading: false,
      },
      optClient: [],
      visibleFields: {}
    };
  },
  computed: {
    ...mapState({
      authUserPermissions: (state) => state.auth.userHasPermissions || [],
    }),
    hasClientData() {
      return this.table.clientOptions.length > 0 && this.companyCode === 'MIND-ID';
    },
    clientFilterOptions() {
      const options = this.table.clientOptions.map(c => ({
        label: c.code || c.value || c.label || c,
        value: c.code || c.value || c
      }));
      options.unshift({ label: "All Clients", value: null });
      return options;
    },
    anyEmptyVal() {
      // return (!this.modal.data.characteristic || !this.modal.data.char_sap || !this.modal.data.single_or_multiple || !this.modal.data.label || !this.modal.data.position || !this.modal.data.space );
      return true
    }
  },
  watch: {
    "table.clientFilter": { handler() { this.syncUrlAndGet(); } },
  },
  mounted() {
    const userAuth = JSON.parse(localStorage.getItem('userAuth') || '{}');
    this.clientCode = userAuth?.user?.client_code || '';
    this.companyCode = userAuth?.user?.company_code || '';
    if (this.hasAnyPermission('sap-connection-view')) {
      this.get();
      this.getClient();
    }
  },
  methods: {
    hasAnyPermission(...perms) {
      return perms.some((p) => this.authUserPermissions.includes(p));
    },
    async get() {
      this.table.onLoad = true
      try {
        const response = await api(this, sapConnectionService.list({
          limit: this.table.limit,
          page: this.table.page.current_page,
          search: this.table.search,
          client: this.companyCode === 'MIND-ID' ? this.table.clientFilter : this.clientCode,
        }), false);

        const resData = response.data || {};
        this.table.data = resData.data || [];
        this.table.total = resData.total || this.table.data.length;
        this.table.page = resData;
        this.table.page.current_page = resData.current_page || 1;
        this.table.page.total_page = resData.last_page || 1;
      } catch (error) {
        this.table.data = []
        this.table.total = 0
      } finally {
        this.table.onLoad = false
      }
    },
    async getClient() {
      try {
        const response = await api(this, clientService.list({ limit: 1000 }), false);

        const list = response.data?.data || response.data || [];
        this.optClient = list;
        this.table.clientOptions = list;
      } catch (error) {
        this.optClient = []
        this.table.clientOptions = []
      }
    },
    syncUrlAndGet() {
      this.$router.push({
        query: {
          ...(this.table.search ? { search: this.table.search } : {}),
          ...(this.table.clientFilter ? { client: this.table.clientFilter } : {}),
        },
      });
      this.table.page.current_page = 1;
      this.get();
    },
    openAddModal() {
      this.modal = {
        visible: true,
        action: "create",
        title: "Add SAP Connection",
        data: { client: "", connection: "", host: "", port: "", instance: "", username: "", password: "", default: "" },
        errorMessage: {},
        onLoading: false,
      }
    },
    openEditModal(item) {
      this.modal = {
        visible: true,
        action: "edit",
        title: "Edit SAP Connection",
        data: { id: item.id, client: item.client, connection: item.connection, host: item.data.host, port: item.data.port, instance: item.data.instance, username: item.data.username, password: "", default: item.default },
        errorMessage: {},
        onLoading: false,
      };
    },
    deleteItem(id) {
      this.confirmDialog("You won't be able to revert this!", "delete it").then((result) => {
        if (result.isConfirmed) {
          this.table.onLoad = true
          api(this, sapConnectionService.delete(id)).then(() => { this.get() });
        }
      });
    },
    setDefault(id) {
      this.confirmDialog("Are you sure you want to set this connection as default?", "set it").then(
        async (result) => {
          if (result.isConfirmed) {
            this.table.onLoad = true
            try {
              await api(this, sapConnectionService.setDefault(id));

              this.get();
            } catch (error) {
              this.handleErrorObjectNested(error)
            }
          }
        }
      );
    },
    save() {
      this.modal.errorMessage = {};
      let isValid = true;
      const requiredField = [{ key: "client", message: "Client" }, { key: "connection", message: "Connection" }, { key: "host", message: "Host" }, { key: "port", message: "Port" }, { key: "instance", message: "Instance" }, { key: "username", message: "Username" }];
      // Password hanya wajib saat create; saat edit boleh dikosongkan (berarti tidak diubah)
      if (this.modal.action === 'create') {
        requiredField.push({ key: "password", message: "Password" });
      }
      requiredField.forEach((val) => {
        if (!this.modal.data[val.key]) {
          this.modal.errorMessage[val.key] = `${val.message} is required`;
          isValid = false;
        }
      })
      if (!isValid) return false;

      const dataPayload = {
        host: this.modal.data.host,
        port: this.modal.data.port,
        username: this.modal.data.username,
        instance: this.modal.data.instance,
      };
      // Password dari API sudah ter-encrypt, jadi jangan dikirim ulang kalau tidak diubah
      if (this.modal.data.password) {
        dataPayload.password = this.modal.data.password;
      }

      const payload = {
        client: this.modal.data.client,
        connection: this.modal.data.connection,
        data: dataPayload,
        default: this.modal.data.default !== "" ? this.modal.data.default : 0
      }

      this.modal.onLoading = true
      setTimeout(async () => {
        try {
          if (this.modal.action === 'create') {
            await api(this, sapConnectionService.create(payload));
          } else if (this.modal.action === 'edit') {
            await api(this, sapConnectionService.update(this.modal.data.id, payload))
          }
          this.modal.data = {};
          this.modal.visible = false;
          this.get();
        } catch (error) {
          this.handleErrorObjectNested(error)
        } finally {
          this.modal.onLoading = false
        }
      }, 500)
    },
    filter() {
      if (this.table.search == this.$route.query.search && this.table.clientFilter == this.$route.query.client) return;
      clearTimeout(this.table.loadTimeout);
      this.table.loadTimeout = setTimeout(() => {
        if (this.table.search && this.table.search.length > 0) {
          this.table.search = this.table.search.toUpperCase()
        }
        this.syncUrlAndGet();
      }, 500)
    },
    changePage() {
      this.table.onLoad = true;
      setTimeout(() => {
        this.get();
      }, 100);
    },
    isFieldVisible(id, field) {
      return !!this.visibleFields[`${id}-${field}`];
    },
    toggleField(id, field) {
      const key = `${id}-${field}`;
      this.visibleFields[key] = !this.visibleFields[key];
    },
    maskValue(value) {
      if (value === null || value === undefined || value === '') return '';
      return '•'.repeat(String(value).length);
    },
    toUpperCaseVal(event, key) {
      const input = event.target;
      const start = input.selectionStart;
      const end = input.selectionEnd;

      const upper = input.value.toUpperCase();
      this.modal.data[key] = upper;

      this.$nextTick(() => {
        input.setSelectionRange(start, end);
      });
    },
    handleErrorObjectNested(error) {
      if (error.response?.data?.code == 400 || error.response?.status == 422 || error.response?.status == 500) {
        const errorData = error.response?.data?.data;

        if (typeof errorData === 'object' && errorData !== null) {
          for (const key in errorData) {
            if (Object.prototype.hasOwnProperty.call(errorData, key)) {
              const match = key.match(/^items\.(\d+)\.(.+)$/);
              if (match) {
                error.response.data.data[key].forEach(message => {
                  this.$toastNotifPrime('error', error.response.data.message, `${message} in row ${parseInt(match[1]) + 1}`)
                })
              } else {
                this.$toastNotifPrime('error', error.response.data.message, errorData[key]);
                // errorData[key].forEach(message => {
                //   this.$toastNotifPrime('error', error.response.data.message, message);
                // });
              }
            }
          }
        }
      }
    },
  },
};
</script>
